import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { CUSTOMERENVIRONMENT } from '@app/common/app-tokens';
import { Constants } from '@app/common/constants';
import { HttpHelper } from '@app/common/http-helper';
import { PageQuery } from '@app/common/page-query';
import { Audit } from '@app/models/audit';
import { Page, PageType } from '@app/models/page';
import { AuditsService } from '@app/services/audits.service';
import { CacheService } from '@app/services/cache.service';
import { NewsService } from '@app/services/news.service';
import { Environment } from '@environments/interface.environment';
import { BehaviorSubject, concat, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, map, shareReplay, takeUntil, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class PagesService implements AuditsService {

  constructor(
    @Inject(CUSTOMERENVIRONMENT) private environment: Environment,
    private http: HttpClient,
    private httpHelper: HttpHelper,
    private cache: CacheService,
    private newsService: NewsService) { }

  private readonly cacheKeyPrefix = 'PagesService-';
  public useCache = true;
  private internalReload$ = new Subject<void>();
  public reload$ = new BehaviorSubject<void>(null);
  public updatedPageFromAudit$ = new Subject<Page>();
  private getAllPages$: Observable<Page[]>;
  private getImages$: Observable<string[]>;
  private getNewsPagesMap$ = new Map<string, Observable<Page[]>>();
  private getNewsPageMap$ = new Map<string, Observable<Page>>();
  private getContentPagesMap$ = new Map<string, Observable<Page[]>>();
  private getContentPageMap$ = new Map<string, Observable<Page>>();
  private getPublishedContentPages$: Observable<Page[]>;
  private getPublishedNewsPages$: Observable<Page[]>;

  public reload() {
    this.getAllPages$ = null;
    this.getPublishedContentPages$ = null;
    this.getPublishedNewsPages$ = null;

    this.getNewsPagesMap$.clear();
    this.getContentPagesMap$.clear();
    this.getNewsPageMap$.clear();
    this.getContentPageMap$.clear();

    this.internalReload$.next();

    if (this.useCache) {
      this.cache.removeAllItemsInPersistentCacheWithGeneratedKeyPrefix(this.cacheKeyPrefix);
    }

    this.reload$.next(null);

    this.newsService.reloadGetTopNewsApi();
    this.newsService.reloadGetPersonalNewsFromApi();
  }

  addPage(page: Page): Observable<Page> {
    // We need to append the siteUrl because there will be an authorization check in the backend against the workplace admin group on this site
    const httpParams = new HttpParams().append('siteUrl', encodeURI(this.environment.coreWorkplaceSiteUrl));
    return this.http
      .post<Page>(`${Constants.API_ENDPOINT}/pages`, page, { params: httpParams })
      .pipe(
        tap(() => {
          this.reload();
        }),
        catchError(this.httpHelper.handleError({ operation: 'addPage', result: null, rethrow: true })));
  }

  getImages(): Observable<Array<string>> {
    if (this.getImages$) {
      return this.getImages$;
    } else {
      const cacheKey = `${this.cacheKeyPrefix}-getImages`;
      this.getImages$ = this.http
        .get<Array<string>>(`${Constants.API_ENDPOINT}/Image`)
        .pipe(
          tap(data => {
            this.useCache && this.cache.set(cacheKey, data);
          }),
          catchError(this.httpHelper.handleError({ operation: 'getImages', result: new Array<string>(), rethrow: true, cacheKey: cacheKey })),
          takeUntil(this.internalReload$),
          shareReplay({ bufferSize: 1, refCount: true })
        );

      if (this.useCache) {
        const cachedData = this.cache.get<Array<string>>(cacheKey);
        if (cachedData) {
          return concat(of(cachedData), this.getImages$);
        }
      }
      return this.getImages$;
    }
  }

  /** returns an url to the uploaded picture */
  addImage(image: File): Observable<string> {
    // We need to append the siteUrl because there will be an authorization check in the backend against the workplace admin group on this site
    const httpParams = new HttpParams().append('siteUrl', encodeURI(this.environment.coreWorkplaceSiteUrl));
    // Because the backend expects data as a multipart form
    const formData = new FormData();
    formData.append('file', image);
    return this.http
      .post<any>(`${Constants.API_ENDPOINT}/Image`, formData,
        {
          params: httpParams,
          observe: 'response',
        })
      .pipe(
        map(data => data.headers.get('location')),
        tap(() => {
          this.reload();
        }),
        catchError(this.httpHelper.handleError({ operation: 'addImage', result: null, rethrow: true })));
  }

  updatePage(id: number, page: Page): Observable<Page> {
    // We need to append the siteUrl because there will be an authorization check in the backend against the workplace admin group on this site
    const httpParams = new HttpParams().append('siteUrl', encodeURI(this.environment.coreWorkplaceSiteUrl));
    return this.http
      .put<Page>(`${Constants.API_ENDPOINT}/pages/${id}`, page, { params: httpParams })
      .pipe(
        tap(() => {
          this.reload();
        }),
        catchError(this.httpHelper.handleError({ operation: 'updatePage', result: null, rethrow: true })));
  }

  deletePage(id: number): Observable<Page> {
    // We need to append the siteUrl because there will be an authorization check in the backend against the workplace admin group on this site
    const httpParams = new HttpParams().append('siteUrl', encodeURI(this.environment.coreWorkplaceSiteUrl));
    const deleteOperation = this.http.delete<Page>(`${Constants.API_ENDPOINT}/pages/${id}`, {

      params: httpParams
    })
      .pipe(
        map(response => {
          console.log(`deleted result: `);
          console.log(response);
          this.reload();
          return response;
        }),
        catchError(this.httpHelper.handleError({ operation: 'deletePage', result: {} as Page })));

    return deleteOperation;
  }

  getPage(id: number): Observable<Page> {
    return this.http
      .get<Page>(`${Constants.API_ENDPOINT}/pages/${id}`)
      .pipe(
        catchError(this.httpHelper.handleError({ operation: 'getPage', result: null, rethrow: true })));
  }

  getAllPages(): Observable<Array<Page>> {
    if (this.getAllPages$) {
      return this.getAllPages$;
    } else {
      const cacheKey = `${this.cacheKeyPrefix}-getAllPages`;
      this.getAllPages$ = this.http
        .get<Array<Page>>(`${Constants.API_ENDPOINT}/pages`)
        .pipe(
          tap(data => {
            this.useCache && this.cache.set(cacheKey, data);
          }),
          catchError(this.httpHelper.handleError({ operation: 'getAllPages', result: new Array<Page>(), rethrow: true, cacheKey: cacheKey })),
          takeUntil(this.internalReload$),
          shareReplay({ bufferSize: 1, refCount: true }));

      if (this.useCache) {
        const cachedData = this.cache.get<Array<Page>>(cacheKey);
        if (cachedData) {
          return concat(of(cachedData), this.getAllPages$);
        }
      }
      return this.getAllPages$;
    }
  }

  getPublishedContentPages(): Observable<Array<Page>> {
    if (this.getPublishedContentPages$) {
      return this.getPublishedContentPages$;
    } else {
      const cacheKey = `${this.cacheKeyPrefix}-getPublishedContentPages`;
      const pq: PageQuery = { PageType: PageType.Content, IncludeDrafts: false, IncludeArchive: false };
      this.getPublishedContentPages$ = this.getPagesByQuery(pq)
        .pipe(
          tap(data => {
            this.useCache && this.cache.set(cacheKey, data);
          }),
          catchError(this.httpHelper.handleError({ operation: 'getPublishedContentPages', result: new Array<Page>(), rethrow: true, cacheKey: cacheKey })),
          takeUntil(this.internalReload$),
          shareReplay({ bufferSize: 1, refCount: true }));

      if (this.useCache) {
        const cachedData = this.cache.get<Array<Page>>(cacheKey);
        if (cachedData) {
          return concat(of(cachedData), this.getPublishedContentPages$);
        }
      }
      return this.getPublishedContentPages$;
    }
  }

  getPublishedNewsPages(): Observable<Array<Page>> {
    if (this.getPublishedNewsPages$) {
      return this.getPublishedNewsPages$;
    } else {
      const cacheKey = `${this.cacheKeyPrefix}-getPublishedNewsPages`;
      const pq: PageQuery = { PageType: PageType.News, IncludeDrafts: false, IncludeArchive: false };
      this.getPublishedNewsPages$ = this.getPagesByQuery(pq)
        .pipe(
          tap(data => {
            this.useCache && this.cache.set(cacheKey, data);
          }),
          catchError(this.httpHelper.handleError({ operation: 'getPublishedContentPages', result: new Array<Page>(), rethrow: true, cacheKey: cacheKey })),
          takeUntil(this.internalReload$),
          shareReplay({ bufferSize: 1, refCount: true }));

      if (this.useCache) {
        const cachedData = this.cache.get<Array<Page>>(cacheKey);
        if (cachedData) {
          return concat(of(cachedData), this.getPublishedNewsPages$);
        }
      }
      return this.getPublishedNewsPages$;
    }
  }

  getContentPages(isFeaturedPage: boolean = false): Observable<Array<Page>> {
    if (this.getContentPagesMap$.has(isFeaturedPage.toString())) {
      return this.getContentPagesMap$.get(isFeaturedPage.toString());
    } else {
      const httpParams = new HttpParams()
        .append('isFeaturedPage', isFeaturedPage.toString());
      const cacheKey = `${this.cacheKeyPrefix}-getContentPages-${isFeaturedPage.toString()}`;
      this.getContentPagesMap$.set(isFeaturedPage.toString(), this.http
        .get<Array<Page>>(`${Constants.API_ENDPOINT}/pages/content`, {
          params: httpParams,
        })
        .pipe(
          tap(data => {
            this.useCache && this.cache.set(cacheKey, data);
          }),
          catchError(this.httpHelper.handleError({ operation: 'getContentPages', result: new Array<Page>(), rethrow: true, cacheKey: cacheKey })),
          takeUntil(this.internalReload$),
          shareReplay({ bufferSize: 1, refCount: true })));

      if (this.useCache) {
        const cachedData = this.cache.get<Array<Page>>(cacheKey);
        if (cachedData) {
          return concat(of(cachedData), this.getContentPagesMap$.get(isFeaturedPage.toString()));
        }
      }
      return this.getContentPagesMap$.get(isFeaturedPage.toString());
    }
  }

  getContentPagesBySearchQuery(query: string, amount: number, isPublished = true): Observable<Array<Page>> {
    const httpParams = new HttpParams()
      .append('query', query)
      .append('amount', amount.toString())
      .append('isPublished', isPublished.toString());
    const fetchOperation = this.http
      .get<Array<Page>>(`${Constants.API_ENDPOINT}/pages/content/search`, {
        params: httpParams,
      })
      .pipe(
        catchError(this.httpHelper.handleError({ operation: 'getContentPagesBySearchQuery', result: new Array<Page>(), rethrow: true })),
        takeUntil(this.internalReload$),
        shareReplay({ bufferSize: 1, refCount: true }));

    return fetchOperation;
  }

  getNewsPages(isFeaturedPage: boolean = false): Observable<Array<Page>> {
    if (this.getNewsPagesMap$.has(isFeaturedPage.toString())) {
      return this.getNewsPagesMap$.get(isFeaturedPage.toString());
    } else {
      const httpParams = new HttpParams()
        .append('isFeaturedPage', isFeaturedPage.toString());
      const cacheKey = `${this.cacheKeyPrefix}-getNewsPages-${isFeaturedPage.toString()}`;
      this.getNewsPagesMap$.set(isFeaturedPage.toString(), this.http
        .get<Array<Page>>(`${Constants.API_ENDPOINT}/pages/news`, {
          params: httpParams,
        })
        .pipe(
          tap(data => {
            this.useCache && this.cache.set(cacheKey, data);
          }),
          catchError(this.httpHelper.handleError({ operation: 'getNewsPages', result: new Array<Page>(), rethrow: true, cacheKey: cacheKey })),
          takeUntil(this.internalReload$),
          shareReplay({ bufferSize: 1, refCount: true })));

      if (this.useCache) {
        const cachedData = this.cache.get<Array<Page>>(cacheKey);
        if (cachedData) {
          return concat(of(cachedData), this.getNewsPagesMap$.get(isFeaturedPage.toString()));
        }
      }
      return this.getNewsPagesMap$.get(isFeaturedPage.toString());
    }
  }

  /*
   * slugTitleWithId should match /[a-z0-9-]+-\d+$/g  e.g (my-nice-title-4)
   */
  getNewsPage(slugTitleWithId: string): Observable<Page> {
    if (/[a-z0-9-]+-\d+$/g.test(slugTitleWithId) !== true) {
      return throwError(`${slugTitleWithId} does not match recognized pattern`);
    }

    const cacheKey = `${this.cacheKeyPrefix}-getNewsPage-${slugTitleWithId}`;

    if (this.getNewsPageMap$.has(cacheKey)) {
      return this.getNewsPageMap$.get(cacheKey);
    } else {
      this.getNewsPageMap$.set(cacheKey, this.http
        .get<Page>(`${Constants.API_ENDPOINT}/pages/news/${slugTitleWithId}`)
        .pipe(
          tap(data => {
            this.useCache && this.cache.set(cacheKey, data);
          }),
          catchError(this.httpHelper.handleError({ operation: 'getNewsPage', result: null, rethrow: true, cacheKey: cacheKey })),
          takeUntil(this.internalReload$),
          shareReplay({ bufferSize: 1, refCount: true })));

      if (this.useCache) {
        const cachedData = this.cache.get<Page>(cacheKey);
        if (cachedData) {
          return concat(of(cachedData), this.getNewsPageMap$.get(cacheKey));
        }
      }
      return this.getNewsPageMap$.get(cacheKey);
    }
  }

  /*
   * slugTitleWithId should match /[a-z0-9-]+-\d+$/g e.g (my-nice-title-4)
   */
  getContentPage(slugTitleWithId: string): Observable<Page> {
    if (/[a-z0-9-]+-\d+$/g.test(slugTitleWithId) !== true) {
      return throwError(`${slugTitleWithId} does not match recognized pattern`);
    }

    const cacheKey = `${this.cacheKeyPrefix}-getContentPage-${slugTitleWithId}`;

    if (this.getContentPageMap$.has(cacheKey)) {
      return this.getContentPageMap$.get(cacheKey);
    } else {
      this.getContentPageMap$.set(cacheKey, this.http
        .get<Page>(`${Constants.API_ENDPOINT}/pages/content/${slugTitleWithId}`)
        .pipe(
          tap(data => {
            this.useCache && this.cache.set(cacheKey, data);
          }),
          catchError(this.httpHelper.handleError({ operation: 'getContentPage', result: null, rethrow: true, cacheKey: cacheKey })),
          takeUntil(this.internalReload$),
          shareReplay({ bufferSize: 1, refCount: true })));

      if (this.useCache) {
        const cachedData = this.cache.get<Page>(cacheKey);
        if (cachedData) {
          return concat(of(cachedData), this.getContentPageMap$.get(cacheKey));
        }
      }
      return this.getContentPageMap$.get(cacheKey);
    }
  }

  getAudits(id: number): Observable<Array<Audit>> {
    // We need to append the siteUrl because there will be an authorization check in the backend against the workplace admin group on this site
    const httpParams = new HttpParams().append('siteUrl', encodeURI(this.environment.coreWorkplaceSiteUrl));
    return this.http.
      get<Array<Audit>>(`${Constants.API_ENDPOINT}/pages/${id}/audits`, { params: httpParams })
      .pipe(
        map(results => {
          return results.map(r => new Audit(r));
        }),
        catchError(this.httpHelper.handleError({ operation: 'getAudits', result: null, rethrow: true }))
      );
  }

  restoreToAudit<Page>(id: number, auditId: number): Observable<Page> {
    // We need to append the siteUrl because there will be an authorization check in the backend against the workplace admin group on this site
    const httpParams = new HttpParams().append('siteUrl', encodeURI(this.environment.coreWorkplaceSiteUrl));
    return this.http.post<Page>(`${Constants.API_ENDPOINT}/pages/${id}/audits/${auditId}/restore`, {}, {
      params: httpParams
    })
      .pipe(
        tap((page) => {
          this.reload();
          this.updatedPageFromAudit$.next(new Page(page));
        }),
        catchError(this.httpHelper.handleError({ operation: 'restoreToAudit', result: null, rethrow: true })));
  }

  getPagesByQuery(q: PageQuery): Observable<Array<Page>> {
    const fetchOperation = this.http.post<Array<Page>>(`${Constants.API_ENDPOINT}/pages/query/`, q, {

    })
      .pipe(
        catchError(this.httpHelper.handleError({ operation: 'query for pages', result: new Array<Page>(), rethrow: true })),
        takeUntil(this.internalReload$));
    return fetchOperation;
  }

}
